import "./_ManufacturerPanel.scss";
import "./_ManufacturerPanelMobile.scss";
import { useEffect, useRef, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";

function _ManufacturerPanel() {
  const [manufacturerName, setManufacturerName] = useState("");
  const [manufacturerLogo, setManufacturerLogo] = useState(""); // preview or existing path
  const [manufacturerData, setManufacturerData] = useState([]);
  const [checked, setChecked] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const safeName = (name) => {
    if (!name) return "";
    // Replace illegal filename characters and trim whitespace
    //console.log(name.replace(/[\/\\:*?"<>|]/g, "_").trim());
    return name.replace(/[\/\\:*?"<>|]/g, "_").trim();
  };
  // 🟢 Fetch manufacturers
  const fetchManufacturers = () => {
    fetch(`${process.env.REACT_APP_BASE_URL}api/product/get-manufacturer`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        setManufacturerData(data.manufacturers || []);
        setChecked(new Array(data.manufacturers.length).fill(false));
      })
      .catch((err) => console.error("Error fetching manufacturers:", err));
  };

  useEffect(() => {
    fetchManufacturers();
  }, []);

  // 🟠 Handle File Selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setManufacturerLogo(URL.createObjectURL(file)); // temporary preview
    }
  };

  const handleSelectImage = () => fileInputRef.current.click();

  // 🟡 Save or Update Manufacturer
  const handleSave = () => {
    if (!manufacturerName.trim()) {
      alert("Manufacturer name cannot be empty.");
      return;
    }

    const formData = new FormData();
    formData.append("manufacturerName", manufacturerName);
    if (selectedFile) formData.append("manufacturerLogo", selectedFile);
    if (isEditing) formData.append("editingId", editingId);

    const url = isEditing
      ? `https://littlekidinside.shop/api/product/update-manufacturer/${editingId}`
      : `https://littlekidinside.shop/api/product/add-manufacturer`;

    fetch(url, {
      method: isEditing ? "PUT" : "POST",
      body: formData,
    })
      .then((res) => res.json())
      .then(() => {
        alert(isEditing ? "Manufacturer updated!" : "Manufacturer saved!");
        handleCancel();
        fetchManufacturers();
      })
      .catch((err) => console.error("Error saving manufacturer:", err));
  };

  // 🧹 Reset form
  const handleCancel = () => {
    setManufacturerName("");
    setSelectedFile(null);
    setManufacturerLogo("");
    setIsEditing(false);
    setEditingId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // 🗑️ Delete Single Manufacturer
  const handleDelete = (item) => {
    if (item.item_count > 0) {
      alert(
        `❌ Cannot delete "${item.PM_Name}" because it still has ${item.item_count} item(s).`,
      );
      return;
    }

    if (!window.confirm(`Delete "${item.PM_Name}"?`)) return;

    fetch(
      `${process.env.REACT_APP_BASE_URL}api/product/delete-manufacturer/${item.PM_ID}`,
      {
        method: "DELETE",
      },
    )
      .then((res) => res.json())
      .then(() => {
        alert("Manufacturer deleted!");
        fetchManufacturers();
      })
      .catch((err) => console.error("Error deleting manufacturer:", err));
  };

  // 🗑️ Delete multiple selected
  const handleDeleteSelected = () => {
    const selectedItems = manufacturerData.filter((_, i) => checked[i]);
    const deletable = selectedItems.filter((i) => i.item_count === 0);
    const blocked = selectedItems.filter((i) => i.item_count > 0);

    if (selectedItems.length === 0)
      return alert("Please select at least one manufacturer.");

    if (blocked.length > 0) {
      alert(
        `❌ These cannot be deleted because they have items:\n${blocked
          .map((b) => b.PM_Name)
          .join(", ")}`,
      );
      return;
    }

    if (!window.confirm(`Delete ${deletable.length} selected manufacturers?`))
      return;

    Promise.all(
      deletable.map((i) =>
        fetch(
          `${process.env.REACT_APP_BASE_URL}api/product/delete-manufacturer/${i.PM_ID}`,
          {
            method: "DELETE",
          },
        ),
      ),
    )
      .then(() => {
        alert(`✅ Deleted ${deletable.length} manufacturer(s)!`);
        fetchManufacturers();
      })
      .catch((err) => console.error("Error deleting selected:", err));
  };

  // ✏️ Edit Manufacturer
  const handleEdit = (item) => {
    setManufacturerName(item.PM_Name);
    setManufacturerLogo(
      item.PM_Logopath
        ? `${process.env.REACT_APP_BASE_URL}Assets/Manufacturer/${safeName(
            item.PM_Name,
          )}/${item.PM_Logopath}`
        : "",
    );
    setIsEditing(true);
    setEditingId(item.PM_ID);
    setSelectedFile(null);
  };

  // ☑️ Checkbox handling
  const handleCheck = (index) => {
    const updated = [...checked];
    updated[index] = !updated[index];
    setChecked(updated);
  };

  const handleCheckAll = (e) => {
    const all = e.target.checked;
    setChecked(new Array(manufacturerData.length).fill(all));
  };

  const allChecked = checked.length > 0 && checked.every(Boolean);

  // 🔍 Search filter (safe check)
  const filteredManufacturers = manufacturerData.filter((item) =>
    (item.PM_Name || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <main className="ad-manu-container">
      <div className="manu-panel-header">
        <div className="manu-panel-title-wrapper">
          <span>MANUFACTURER PANEL</span>
        </div>
      </div>

      <div className="manu-panel-container">
        <div className="manu-list">
          <div className="actions">
            {checked.some((v) => v) && (
              <button
                className="delete-selected-btn"
                onClick={handleDeleteSelected}
              >
                DELETE
              </button>
            )}
            <input
              type="text"
              placeholder="Keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="manu-panel-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>NO.</th>
                  <th>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={handleCheckAll}
                    />
                  </th>
                  <th>Logo</th>
                  <th>Manufacturer Name</th>
                  <th>Items</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredManufacturers.length > 0 ? (
                  filteredManufacturers.map((item, index) => (
                    <tr
                      key={item.PM_ID}
                      className={editingId === item.PM_ID ? "editing" : ""}
                    >
                      <td>{index + 1}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={checked[index] || false}
                          onChange={() => handleCheck(index)}
                        />
                      </td>
                      <td>
                        {item.PM_Logopath ? (
                          <img
                            src={`${
                              process.env.REACT_APP_BASE_URL
                            }Assets/Manufacturer/${safeName(item.PM_Name)}/${
                              item.PM_Logopath
                            }`}
                            alt="logo"
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "8px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{item.PM_Name}</td>
                      <td>{item.item_count}</td>
                      <td>
                        <div className="td-action-btns-wrapper">
                          <FaEdit
                            className="action-icon edit"
                            onClick={() => handleEdit(item)}
                          />
                          <FaTrash
                            className="action-icon delete"
                            onClick={() => handleDelete(item)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center" }}>
                      No manufacturers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="add-manu">
          <div className="am-input-wrapper">
            <label>
              {isEditing ? "Edit Manufacturer:" : "Add Manufacturer:"}
            </label>
            <input
              type="text"
              value={manufacturerName}
              onChange={(e) => setManufacturerName(e.target.value)}
            />
          </div>

          <div className="am-input-wrapper">
            <button onClick={handleSelectImage}>
              {isEditing ? "Change Image" : "Upload Image"}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
            <p>FILE EXTENSION: .JPG, .JPEG, .PNG</p>
            {manufacturerLogo && (
              <img
                src={manufacturerLogo}
                alt="Preview"
                className="logo-preview"
              />
            )}
          </div>

          <div className="am-btn-action-wrapper">
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
            <button className="save-btn" onClick={handleSave}>
              {isEditing ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}

export default _ManufacturerPanel;
